import * as React from 'react'
import * as Redux from 'react-redux'

import { styled, css } from 'next-yak'

import { useAuth } from '@owl-nest/redux-wrapper/hooks'
import * as api from '@owl-nest/api-client/latest'
import { t } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import { SearchQualifier } from '@owl-nest/api-client/latest'
import { QueryStatus, useQuery } from '@owl-nest/hooks'

import { BREAKPOINTS, Spinner, styles as S } from '@ulule/owl-kit-components/next'

import * as projectModel from '../../../models/project'
import { RootState } from '../../../../reducers'
import { SuggestedProject } from './SuggestedProject'

type SuggestedProjectsListProps = {
  isEditMode: boolean
}

export function SuggestedProjectsList({ isEditMode }: SuggestedProjectsListProps): React.ReactElement<unknown> {
  const auth = useAuth()

  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const userIsAuthenticated = auth.type === 'loggedin'

  const breakpoint = isEditMode ? 'laptop-l' : 'laptop'

  const querySearch = getQuerySearch({
    search: '',
    qualifiers: {
      sort: 'popular',
      tag_id: project?.main_tag?.id,
      status: 'currently',
    },
    params: { limit: 8 },
    withAccessToken: userIsAuthenticated,
  })

  const [query, response] = useQuery(async () => {
    const result = await api.api.get.search({
      getParams: querySearch.toString(),
      withToken: userIsAuthenticated,
    })
    return result.next((success) => success.body)
  })

  if (response.status === QueryStatus.PRISTINE) {
    query()
  }

  if (response.status !== QueryStatus.SUCCESS) {
    return (
      <Content $breakpoint={breakpoint}>
        <Spinner />
      </Content>
    )
  }

  return (
    <Content $breakpoint={breakpoint}>
      <S.heading.M>
        {t('Discover more %(mainTag)s live projects', {
          mainTag: model.project.mainTag<projectModel.ExtraFields>(project)?.toLowerCase(),
        })}
      </S.heading.M>
      <ProjectListItems $breakpoint={breakpoint}>
        {response.data.projects?.map((suggestedProject) => {
          return <SuggestedProject key={suggestedProject.id} project={suggestedProject} />
        })}
      </ProjectListItems>
      <Center>
        <S.button.Button as="a" href={project?.main_tag?.absolute_url} kind="secondary">
          {t('See more')}
        </S.button.Button>
      </Center>
    </Content>
  )
}

const contentBreakpointStyle = css`
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 0 auto;

  ${S.heading.M} {
    margin: 0;
  }

  ${SuggestedProject} {
    margin: 0;
    width: auto;
  }
`

const Content = styled.div<{ $breakpoint: 'laptop-l' | 'laptop' }>`
  padding-top: 35px;

  ${S.heading.M} {
    text-align: center;
    margin: 0 20px;
  }

  ${SuggestedProject} {
    margin: 20px;
    width: 100%;
  }

  ${({ $breakpoint }) => {
    if ($breakpoint === 'laptop-l') {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP_L} {
          ${contentBreakpointStyle};
        }
      `
    }
    if ($breakpoint === 'laptop') {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${contentBreakpointStyle};
        }
      `
    }
  }}
`

const Center = styled.div`
  text-align: center;
`

const projectListItemsBreakpointStyle = css`
  align-items: initial;
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  grid-gap: 25px;
  grid-auto-rows: minmax(100px, auto);
  margin: 50px;
  margin-bottom: 0px;
`

const ProjectListItems = styled.div<{ $breakpoint: 'laptop-l' | 'laptop' }>`
  display: flex;
  flex-wrap: wrap;
  margin-top: 5px;
  padding-bottom: 40px;
  padding-top: 25px;
  justify-content: space-around;

  ${({ $breakpoint }) => {
    if ($breakpoint === 'laptop-l') {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP_L} {
          ${projectListItemsBreakpointStyle};
        }
      `
    }
    if ($breakpoint === 'laptop') {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${projectListItemsBreakpointStyle};
        }
      `
    }
  }}
`

const projectSearchExtraFields = {
  guests: ['main_image', 'main_tag', 'owner', 'partnerships', 'avatar_image'],
  users: ['user_role', 'main_image', 'main_tag', 'owner', 'partnerships', 'avatar_image'],
}

type GetQuerySearchProps = {
  search: string
  qualifiers: SearchQualifier
  params: { [s: string]: boolean | number | string }
  withAccessToken: boolean
}

export const getQuerySearch = ({
  search = '',
  qualifiers = {},
  params,
  withAccessToken,
}: GetQuerySearchProps): URLSearchParams => {
  const extraFields = withAccessToken ? projectSearchExtraFields.users : projectSearchExtraFields.guests
  const paramsUrl = new URLSearchParams()
  const request = search ? `${search} ${stringifyQualifiers(qualifiers)}` : `${stringifyQualifiers(qualifiers)}`
  paramsUrl.append('q', request)
  paramsUrl.append('extra_fields', extraFields.join(','))

  Object.keys(params).map((name) => {
    paramsUrl.append(name, params[name].toString())
  })

  return paramsUrl
}

function stringifyQualifiers(qualifiers: SearchQualifier): string {
  return Object.entries(qualifiers)
    .map(([key, value]) => {
      if (Array.isArray(value)) {
        return (value as []).map((value: string) => `${key}:${value}`).join(' ')
      } else {
        return `${key}:${value}`
      }
    })
    .join(' ')
}
